import { useEffect, useMemo, useRef, useState } from "react";
import sondertagService from "../services/sondertagService";
import { useAppContext } from "../../../2_ksbHub/context/appContext";

export default function useSondertagState() {
  const { currentUser, setSonderEventLoading } = useAppContext();

  const [schoolyears, setSchoolyears] = useState([]);
  const [selectedSchoolyear, setSelectedSchoolyear] = useState(null);
  const [statsAngebote, setStatsAngebote] = useState([]);

  const [sonderEvents, setSonderEvents] = useState([]); // ✅ NEW
  const [selectedSonderEvent, setSelectedSonderEvent] = useState(null); // ✅ NEW

  const [sondertage, setSondertage] = useState([]);

  const selectedSchoolyearIdRef = useRef(null);
  const selectedSonderEventIdRef = useRef(null);

  const [teacherWorkloads, setTeacherWorkloads] = useState({});

  const angebote = useMemo(() => {
    return sondertage.flatMap((day) =>
      day.jahrgaenge.flatMap((j) =>
        j.angebote.map((a) => ({
          ...a,
          jahrgangId: j.id,
          jahrgangTitle: j.title,
          sondertagId: day.id,
          sondertagLabel: day.label,
          eventId: day.eventId,
        })),
      ),
    );
  }, [sondertage]);

  const jobs = useMemo(() => {
    return sondertage.flatMap((day) =>
      day.jahrgaenge.map((jg) => ({
        ...jg,
        sondertagId: day.id,
      })),
    );
  }, [sondertage]);

  /**
   * =========================================================
   * ✅ keep ref updated
   * =========================================================
   */
  useEffect(() => {
    selectedSchoolyearIdRef.current = selectedSchoolyear?.id;
  }, [selectedSchoolyear]);
  useEffect(() => {
    selectedSonderEventIdRef.current = selectedSonderEvent?.id;
  }, [selectedSonderEvent]);

  /**
   * =========================================================
   * ✅ reset on logout
   * =========================================================
   */
  useEffect(() => {
    if (!currentUser) {
      setSchoolyears([]);
      setSelectedSchoolyear(null);
      setStatsAngebote([]);
      setTeacherWorkloads({});

      setSonderEvents([]);
      setSelectedSonderEvent(null);

      setSondertage([]);
    }
  }, [currentUser]);

  /**
   * =========================================================
   * ✅ load schoolyears
   * =========================================================
   */
  useEffect(() => {
    if (!currentUser) return;

    const loadSchoolyears = async () => {
      const res = await sondertagService.getSchoolyears();
      setSchoolyears(res.reverse());
      if (res.length > 0) {
        setSelectedSchoolyear(res[0]);
      }
    };

    loadSchoolyears();
  }, [currentUser]);

  /**
   * =========================================================
   * ✅ load sondertagS (NEW STEP ✅)
   * =========================================================
   */

  function flattenAngebote(data) {
    const result = [];

    data.forEach((day) => {
      day.jahrgaenge.forEach((j) => {
        j.angebote.forEach((a) => {
          result.push(a);
        });
      });
    });

    return result;
  }

  useEffect(() => {
    if (!selectedSchoolyear?.id) return;

    // ✅ CLEAR OLD DATA
    setSelectedSonderEvent(null);
    setSondertage([]);
    setStatsAngebote([]);
    setTeacherWorkloads({});

    const loadSondertage = async () => {
      try {
        const res = await sondertagService.getSonderEvents(
          selectedSchoolyear.id,
        );

        setSonderEvents(res);

        if (res.length > 0) {
          setSelectedSonderEvent(res[0]);
        } else {
          setSelectedSonderEvent(null);
        }
      } catch (err) {
        console.error("Error loading sonderEvents:", err);
        setSonderEvents([]);
        setSelectedSonderEvent(null);
      }
    };

    // ✅ NEW: stats load
    const loadStats = async () => {
      try {
        const data = await sondertagService.getStatsData(selectedSchoolyear.id);

        const flat = flattenAngebote(data);
        setStatsAngebote(flat);
      } catch (err) {
        console.error("Error loading stats:", err);
        setStatsAngebote([]);
      }
    };

    loadTeacherWorkloads(selectedSchoolyear.id);
    loadSondertage();
    loadStats();
  }, [selectedSchoolyear?.id]);

  /**
   * =========================================================
   * ✅ load sonderEvent data
   * =========================================================
   */
  const loadSonderEventData = async (eventIdParam) => {
    const eventId = eventIdParam || selectedSonderEvent?.id;
    if (!eventId) return;

    try {
      const data = await sondertagService.getSonderEventData(eventId);

      // ✅ ONLY THIS
      setSondertage(data);

      // ✅ IMPORTANT FIX
      if (!selectedSchoolyear?.id) {
        console.warn("⚠️ No selectedSchoolyear yet");
        return;
      }

      // ✅ reload events (if needed)
      const events = await sondertagService.getSonderEvents(
        selectedSchoolyear.id,
      );
      setSonderEvents(events);
    } catch (err) {
      console.error("❌ Error loading Sondertage:", err);
    }
  };

  /**
   * =========================================================
   * ✅ load loadTeacherWorkload data
   * =========================================================
   */
  const loadTeacherWorkloads = async (schoolyearId) => {
    if (!schoolyearId) {
      setTeacherWorkloads({});
      return;
    }
    try {
      const rows = await sondertagService.getTeacherStats(schoolyearId);

      const workloads = {};

      rows.forEach((row) => {
        workloads[row.abbreviation.toLowerCase()] = {
          ...row,
          name: row.teacher_name,
          workload: Number(row.workload),
        };
      });

      setTeacherWorkloads(workloads);
    } catch (err) {
      console.error(err);
    }
  };

  /**
   * =========================================================
   * ✅ initial load (NOW BASED ON sondertag ✅)
   * =========================================================
   */
  useEffect(() => {
    if (!currentUser?.id || !selectedSonderEvent?.id) return;
    const loadData = async () => {
      setSonderEventLoading(true); // ✅ start loading

      try {
        await loadSonderEventData(selectedSonderEvent.id);
      } finally {
        setSonderEventLoading(false); // ✅ finish loading
      }
    };

    loadData();
  }, [currentUser, selectedSonderEvent]);

  /**
   * =========================================================
   * ✅ RETURN API
   * =========================================================
   */
  return {
    schoolyears,
    setSchoolyears,
    selectedSchoolyear,
    setSelectedSchoolyear,
    selectedSchoolyearIdRef,
    selectedSonderEventIdRef,
    sonderEvents,
    setSonderEvents,
    selectedSonderEvent,
    setSelectedSonderEvent,

    sondertage,
    setSondertage,
    jobs,
    angebote,
    setStatsAngebote,
    statsAngebote,
    loadSonderEventData,
    loadTeacherWorkloads,
    teacherWorkloads,
    setTeacherWorkloads,
  };
}
